import { useState, useEffect, useRef, useCallback } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { X, Search, MapPin, Loader2 } from "lucide-react";

// Fix Leaflet's default broken icon in bundlers
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Fort Worth, TX as default centre
const DEFAULT_CENTER = [32.7357, -97.1081];

function MapCenterController({ center }) {
  const map = useMap();
  const prev = useRef(center);
  useEffect(() => {
    if (prev.current[0] !== center[0] || prev.current[1] !== center[1]) {
      map.setView(center, 15);
      prev.current = center;
    }
  }, [center, map]);
  return null;
}

function DraggableMarker({ position, onPositionChange }) {
  const markerRef = useRef(null);
  return (
    <Marker
      draggable
      position={position}
      ref={markerRef}
      eventHandlers={{
        dragend() {
          const pos = markerRef.current?.getLatLng();
          if (pos) onPositionChange(pos.lat, pos.lng);
        },
      }}
    />
  );
}

function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

export default function LocationPickerModal({
  isOpen,
  onClose,
  onConfirm,
  initialLocation = "",
  initialLat = null,
  initialLng = null,
}) {
  const [searchQuery, setSearchQuery] = useState(initialLocation);
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const [position, setPosition] = useState(
    initialLat && initialLng ? [initialLat, initialLng] : DEFAULT_CENTER
  );
  const [selectedAddress, setSelectedAddress] = useState(initialLocation);
  const debounceRef = useRef(null);

  // Reset state whenever the modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery(initialLocation);
      setSelectedAddress(initialLocation);
      setPosition(initialLat && initialLng ? [initialLat, initialLng] : DEFAULT_CENTER);
      setSuggestions([]);
    }
  }, [isOpen, initialLocation, initialLat, initialLng]);

  const searchNominatim = useCallback(async (query) => {
    if (!query.trim() || query.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    setIsSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`,
        { headers: { "Accept-Language": "en" } }
      );
      const data = await res.json();
      setSuggestions(Array.isArray(data) ? data : []);
    } catch {
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchNominatim(val), 300);
  };

  const handleSelectSuggestion = (item) => {
    const lat = parseFloat(item.lat);
    const lng = parseFloat(item.lon);
    setPosition([lat, lng]);
    setSelectedAddress(item.display_name);
    setSearchQuery(item.display_name);
    setSuggestions([]);
  };

  const handlePositionChange = async (lat, lng) => {
    setPosition([lat, lng]);
    setIsReverseGeocoding(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
        { headers: { "Accept-Language": "en" } }
      );
      const data = await res.json();
      const addr = data.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      setSelectedAddress(addr);
      setSearchQuery(addr);
    } catch {
      const fallback = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      setSelectedAddress(fallback);
      setSearchQuery(fallback);
    } finally {
      setIsReverseGeocoding(false);
    }
  };

  const handleConfirm = () => {
    onConfirm({
      location: selectedAddress,
      latitude: position[0],
      longitude: position[1],
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="font-popinsSemiBold text-lg">Pick Location</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
          >
            <X size={20} className="text-black/50" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-4 pb-2 relative">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search for an address..."
              className="w-full pl-9 pr-9 py-2 border-2 border-gray-200 rounded-md font-popinsRegular text-sm outline-none focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] transition-all"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {(isSearching || isReverseGeocoding) && (
              <Loader2
                size={16}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-black/40 animate-spin pointer-events-none"
              />
            )}
          </div>

          {/* Suggestions dropdown */}
          {suggestions.length > 0 && (
            <div className="absolute z-[10000] left-5 right-5 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
              {suggestions.map((s) => (
                <button
                  key={s.place_id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelectSuggestion(s)}
                  className="w-full text-left px-4 py-2.5 text-sm font-popinsRegular hover:bg-gray-50 border-b last:border-0 border-gray-100 transition-colors"
                >
                  {s.display_name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Map */}
        <div className="px-5 pb-2">
          <MapContainer
            center={position}
            zoom={13}
            style={{ height: "300px", width: "100%", borderRadius: "8px" }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              subdomains="abcd"
              maxZoom={19}
            />
            <MapCenterController center={position} />
            <DraggableMarker position={position} onPositionChange={handlePositionChange} />
            <MapClickHandler onMapClick={handlePositionChange} />
          </MapContainer>
          <p className="text-xs text-black/40 font-popinsRegular mt-1.5">
            Click on the map or drag the pin to set the exact location
          </p>
        </div>

        {/* Selected address preview */}
        {selectedAddress && (
          <div className="px-5 pb-3 flex items-start gap-2">
            <MapPin size={15} className="text-[#3FC4E3] flex-shrink-0 mt-0.5" />
            <p className="text-sm font-popinsRegular text-black/70 line-clamp-2">
              {selectedAddress}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md font-popinsMedium text-sm text-black/60 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedAddress}
            className="px-4 py-2 bg-[#3FC4E3] rounded-md font-popinsMedium text-sm text-white hover:bg-[#35b0cc] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Confirm Location
          </button>
        </div>
      </div>
    </div>
  );
}
